<div class="product-detail-container">
  <p-toast position="bottom-right"></p-toast>

  <!-- Loading State -->
  @if (loading()) {
    <div class="loading-container">
      <p-progressSpinner></p-progressSpinner>
      <p class="loading-text">Loading product details...</p>
    </div>
  }

  <!-- Error State -->
  @if (error() && !loading()) {
    <div class="error-container">
      <div class="error-content">
        <div class="error-icon">⚠️</div>
        <h3>Oops! Something went wrong</h3>
        <p>{{ error() }}</p>
        <p-button label="Go Back" (onClick)="goBack()" [raised]="true" severity="primary"></p-button>
      </div>
    </div>
  }

  <!-- Product Detail Content -->
  @if (product() && !loading() && !error()) {
    <!-- Breadcrumb -->
    <div class="breadcrumb">
      <div class="container">
        <nav class="breadcrumb-nav">
          <a (click)="goBack()" class="breadcrumb-link">Store</a>
          <span class="breadcrumb-separator">›</span>
          @if (product()!.productCategory) {
            <span class="breadcrumb-link">{{ product()!.productCategory!.name }}</span>
            <span class="breadcrumb-separator">›</span>
          }
          <span class="breadcrumb-current">{{ product()!.name }}</span>
        </nav>
      </div>
    </div>

    <div class="container">
      <!-- Back Button -->
      <button class="back-button" (click)="goBack()">
        <i class="pi pi-arrow-left"></i>
        Back to Store
      </button>

      <div class="product-detail-layout">
        
        <!-- Product Images -->
        <div class="product-images">
          <div class="main-image-container">
            <img 
              [src]="getCurrentImage()" 
              [alt]="product()!.name"
              class="main-image"
              (error)="$any($event.target).src='/assets/placeholder-product.png'">
            
            <!-- Image Navigation -->
            @if (productImages().length > 1) {
              <button 
                class="image-nav prev" 
                (click)="previousImage()">
                <i class="pi pi-chevron-left"></i>
              </button>
              <button 
                class="image-nav next" 
                (click)="nextImage()">
                <i class="pi pi-chevron-right"></i>
              </button>
            }

            <!-- Discount Badge -->
            @if (getDiscountPercentage()) {
              <div class="discount-badge">
                -{{ getDiscountPercentage() }}%
              </div>
            }

            <!-- Featured Badge -->
            @if (product()!.isFeatured) {
              <div class="featured-badge">
                Featured
              </div>
            }
          </div>

          <!-- Thumbnail Gallery -->
          @if (productImages().length > 1) {
            <div class="thumbnail-gallery">
              @for (image of productImages(); track $index) {
                <button 
                  class="thumbnail"
                  [class.active]="$index === selectedImageIndex()"
                  (click)="selectImage($index)">
                  <img [src]="image" [alt]="product()!.name + ' ' + ($index + 1)" 
                       (error)="$any($event.target).src='/assets/placeholder-product.png'">
                </button>
              }
            </div>
          }
        </div>

        <!-- Product Info -->
        <div class="product-info">
          
          <!-- Product Header -->
          <div class="product-header">
            @if (product()!.productCategory) {
              <div class="product-category">
                {{ product()!.productCategory!.name }}
              </div>
            }
            <h1 class="product-name">{{ product()!.name }}</h1>
            @if (product()!.brand) {
              <div class="product-brand">
                by {{ product()!.brand!.name }}
              </div>
            }
          </div>

          <!-- Product Rating & Reviews -->
          @if (product()?.averageRating && (product()?.averageRating ?? 0) > 0) {
            <div class="product-rating">
              <div class="stars" (click)="scrollToReviews()" title="Click to view reviews">
                @for (star of [1,2,3,4,5]; track star) {
                  <span 
                    class="star" 
                    [class.filled]="star <= Math.floor(product()!.averageRating!)"
                    [class.half]="star === Math.ceil(product()!.averageRating!) && product()!.averageRating! % 1 !== 0">
                    ★
                  </span>
                }
              </div>
              <span class="rating-text">
                ({{ product()!.averageRating!.toFixed(1) }}) • {{ product()!.totalRatings || 0 }} {{ (product()!.totalRatings || 0) === 1 ? 'review' : 'reviews' }}
              </span>
              @if ((product()!.totalRatings || 0) > 0) {
                <a class="view-reviews-link" (click)="scrollToReviews()">
                  View all reviews
                </a>
              }
            </div>
          }

          <!-- Price -->
          <div class="product-price">
            <span class="current-price">{{ formatPrice(product()!.price) }}</span>
            @if (product()?.compareAtPrice && (product()?.compareAtPrice ?? 0) > product()!.price) {
              <span class="original-price">
                {{ formatPrice(product()!.compareAtPrice!) }}
              </span>
            }
          </div>

          <!-- Stock Status -->
          <div class="stock-status" [class]="getStockStatusClass()">
            {{ getStockStatus() }}
          </div>

          <!-- Product Description -->
          @if (product()!.description) {
            <div class="product-description">
              <h3>Description</h3>
              <p>{{ product()!.description }}</p>
            </div>
          }

          <!-- Product Details -->
          <div class="product-details">
            <div class="detail-item">
              <span class="detail-label">SKU:</span>
              <span class="detail-value">{{ product()!.sku }}</span>
            </div>
            
            <div class="detail-item">
              <span class="detail-label">Delivery:</span>
              <span class="detail-value">{{ getDeliveryEstimate() }}</span>
            </div>

            @if (product()!.installationAvailable) {
              <div class="detail-item">
                <span class="detail-label">Installation:</span>
                <span class="detail-value">{{ getInstallationInfo() }}</span>
              </div>
            }

            <div class="detail-item">
              <span class="detail-label">Shipping:</span>
              <span class="detail-value">
                {{ product()!.requiresShipping ? 'Requires shipping' : 'Digital product' }}
              </span>
            </div>
          </div>

          <!-- Quantity Selector -->
          <div class="quantity-selector">
            <label class="quantity-label">Quantity:</label>
            <div class="quantity-controls">
              <button 
                class="quantity-btn" 
                (click)="decreaseQuantity()"
                [disabled]="quantity() <= 1">
                -
              </button>
              <input 
                type="number" 
                [value]="quantity()" 
                (input)="onQuantityChange($event)"
                [max]="product()!.stockQuantity"
                class="quantity-input">
              <button 
                class="quantity-btn" 
                (click)="increaseQuantity()"
                [disabled]="quantity() >= product()!.stockQuantity">
                +
              </button>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <button 
              class="btn btn-primary add-to-cart"
              [disabled]="product()!.stockQuantity === 0 || addingToCart()"
              (click)="addToCart()">
              <i class="pi pi-shopping-cart"></i>
              <span>
                @if (!addingToCart()) {
                  {{ product()!.stockQuantity === 0 ? 'Out of Stock' : 'Add to Cart' }}
                } @else {
                  Adding...
                }
              </span>
            </button>

            <button 
              class="btn btn-secondary wishlist"
              [disabled]="addingToWishlist() || checkingWishlist()"
              (click)="toggleWishlist()">
              <i class="pi" [class.pi-heart]="!isInWishlist()" [class.pi-heart-fill]="isInWishlist()"></i>
              <span>
                @if (!addingToWishlist() && !checkingWishlist()) {
                  {{ isInWishlist() ? 'Remove from Wishlist' : 'Add to Wishlist' }}
                } @else if (addingToWishlist()) {
                  Processing...
                } @else {
                  Checking...
                }
              </span>
            </button>

            <button 
              class="btn btn-secondary share"
              (click)="shareProduct()">
              <i class="pi pi-share-alt"></i>
              Share
            </button>
          </div>

          <!-- Product Features -->
          <div class="product-features">
            <div class="feature">
              <i class="pi pi-check-circle"></i>
              <span>30-day return policy</span>
            </div>

            <div class="feature">
              <i class="pi pi-check-circle"></i>
              <span>Secure payment processing</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Product Ratings -->
      <div class="product-ratings" id="reviews">
        @if (product() && platformId()) {
          <app-product-rating 
            [productId]="product()!.id" 
            [platformId]="platformId()"
            [showCreateForm]="false">
          </app-product-rating>
        }
      </div>

      <!-- Related Products -->
      @if (relatedProducts().length > 0) {
        <div class="related-products">
          <h2>Related Products</h2>
          
          @if (loadingRelated()) {
            <div class="loading-related">
              <p-progressSpinner></p-progressSpinner>
              <p>Loading related products...</p>
            </div>
          }

          @if (!loadingRelated()) {
            <div class="related-products-grid">
              @for (relatedProduct of relatedProducts(); track relatedProduct.id) {
                <div 
                  class="related-product-card" 
                  (click)="navigateToProduct(relatedProduct.id)">
                  
                  <div class="related-product-image">
                    @if (relatedProduct.images) {
                      <img [src]="getRelatedProductImage(relatedProduct)" [alt]="relatedProduct.name"
                           (error)="$any($event.target).src='/assets/placeholder-product.png'">
                    } @else {
                      <img src="/assets/placeholder-product.png" [alt]="relatedProduct.name">
                    }
                    
                    @if (relatedProduct.compareAtPrice && relatedProduct.compareAtPrice > relatedProduct.price) {
                      <div class="related-product-badge">
                        -{{ Math.round(((relatedProduct.compareAtPrice - relatedProduct.price) / relatedProduct.compareAtPrice) * 100) }}%
                      </div>
                    }
                  </div>

                  <div class="related-product-info">
                    <h4 class="related-product-name">{{ relatedProduct.name }}</h4>
                    <div class="related-product-price">
                      <span class="current-price">{{ formatPrice(relatedProduct.price) }}</span>
                      @if (relatedProduct.compareAtPrice && relatedProduct.compareAtPrice > relatedProduct.price) {
                        <span class="original-price">
                          {{ formatPrice(relatedProduct.compareAtPrice) }}
                        </span>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
  }
</div>
